import { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { formatAddress } from '../lib/blockchain';
import { useDEX } from '../hooks/useDEX';

const getWalletAddress = (props) =>
  props?.account ||
  props?.wallet?.account ||
  props?.wallet?.address ||
  props?.walletState?.account ||
  props?.walletState?.address ||
  props?.metaMask?.account ||
  props?.metaMask?.address ||
  '';

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.pools)) return value.pools;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const formatValue = (value, fallback = '—') => {
  if (value === undefined || value === null || value === '') return fallback;
  return `${value}`;
};

const poolId = (pool) =>
  pool?.id || pool?.pool_id || pool?.symbol || pool?.pair || pool?.name || 'Unknown pool';

export default function DEX(props) {
  const account = getWalletAddress(props);
  const { price, pools, loading, error, refreshPools, getPrice, swap } = useDEX();

  const [tokenIn, setTokenIn] = useState('ITA');
  const [tokenOut, setTokenOut] = useState('BTC');
  const [amountIn, setAmountIn] = useState('');
  const [oraclePayload, setOraclePayload] = useState(null);
  const [swapFeedback, setSwapFeedback] = useState({ type: '', message: '' });

  useEffect(() => {
    const loadOracle = async () => {
      try {
        const response = await api.oracle.price();
        setOraclePayload(response);
      } catch {
        setOraclePayload(null);
      }
    };

    loadOracle();
  }, []);

  useEffect(() => {
    getPrice(tokenIn, tokenOut);
  }, [getPrice, tokenIn, tokenOut]);

  const poolRows = useMemo(() => asArray(pools), [pools]);

  const handleSwap = async (event) => {
    event.preventDefault();
    setSwapFeedback({ type: '', message: '' });

    if (!account) {
      setSwapFeedback({
        type: 'error',
        message: 'Connect a wallet before submitting a swap.',
      });
      return;
    }

    if (!amountIn || Number(amountIn) <= 0) {
      setSwapFeedback({
        type: 'error',
        message: 'Enter a valid input amount.',
      });
      return;
    }

    try {
      const response = await swap(tokenIn, tokenOut, amountIn, account);
      setSwapFeedback({
        type: 'success',
        message:
          response?.message ||
          response?.result ||
          `Swap submitted from ${tokenIn} to ${tokenOut}.`,
      });
      setAmountIn('');
    } catch (err) {
      setSwapFeedback({
        type: 'error',
        message: err?.response?.data?.error || err?.message || 'Swap request failed.',
      });
    }
  };

  const oracleLabel =
    oraclePayload?.pair || oraclePayload?.symbol || oraclePayload?.market || 'Oracle feed';

  return (
    <div className="space-y-6">
      <section className="glass p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm uppercase tracking-wide text-slate-400">DEX</p>
            <h1 className="mt-2 text-3xl font-semibold text-white">Swap against live exchange pools</h1>
            <p className="mt-2 text-slate-300">
              The UI only calls the exchange endpoints currently exposed by the HudLife backend.
            </p>
          </div>

          <button
            type="button"
            className="rounded-full border border-white/10 px-4 py-2 text-sm text-white hover:bg-white/5"
            onClick={() => refreshPools()}
          >
            Refresh pools
          </button>
        </div>
      </section>

      {error || swapFeedback.message ? (
        <section
          className={`glass p-4 ${
            swapFeedback.type === 'error' || error
              ? 'border border-rose-500/30 text-rose-200'
              : 'border border-emerald-500/30 text-emerald-200'
          }`}
        >
          {swapFeedback.message || error}
        </section>
      ) : null}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <article className="glass p-5">
          <p className="text-sm text-slate-400">Oracle price</p>
          <h2 className="mt-2 text-2xl font-semibold text-white">{formatValue(price, 'Unavailable')}</h2>
          <p className="mt-1 text-sm text-slate-400">{oracleLabel}</p>
        </article>

        <article className="glass p-5">
          <p className="text-sm text-slate-400">Wallet sender</p>
          <h2 className="mt-2 text-2xl font-semibold text-white">
            {account ? formatAddress(account) : 'Not connected'}
          </h2>
        </article>

        <article className="glass p-5">
          <p className="text-sm text-slate-400">Available pools</p>
          <h2 className="mt-2 text-2xl font-semibold text-white">{poolRows.length}</h2>
          <p className="mt-1 text-sm text-slate-400">Loaded from /api/exchange/pools</p>
        </article>

        <article className="glass p-5">
          <p className="text-sm text-slate-400">Status</p>
          <h2 className="mt-2 text-2xl font-semibold text-white">{loading ? 'Syncing…' : 'Ready'}</h2>
          <p className="mt-1 text-sm text-slate-400">
            Pool and price refreshes are safe read calls until you submit a swap.
          </p>
        </article>
      </section>

      <section className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <article className="glass p-6">
          <h2 className="text-xl font-semibold text-white">Swap form</h2>

          <form className="mt-4 space-y-4" onSubmit={handleSwap}>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm text-slate-400">Token in</span>
                <input
                  className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none"
                  value={tokenIn}
                  onChange={(event) => setTokenIn(event.target.value.toUpperCase())}
                  placeholder="ITA"
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-sm text-slate-400">Token out</span>
                <input
                  className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none"
                  value={tokenOut}
                  onChange={(event) => setTokenOut(event.target.value.toUpperCase())}
                  placeholder="BTC"
                />
              </label>
            </div>

            <label className="block">
              <span className="mb-2 block text-sm text-slate-400">Amount in</span>
              <input
                type="number"
                min="0"
                step="any"
                className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none"
                value={amountIn}
                onChange={(event) => setAmountIn(event.target.value)}
                placeholder="0.0"
              />
            </label>

            <div className="rounded-2xl bg-white/5 p-4 text-sm text-slate-300">
              Payload preview: {'{'} pool_id, address, amount, direction {'}'}
            </div>

            <button
              type="submit"
              className="rounded-full bg-indigo-500 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-400"
            >
              Submit swap
            </button>
          </form>
        </article>

        <article className="glass p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white">Pool list</h2>
            <span className="text-sm text-slate-400">Read-only market snapshot</span>
          </div>

          {loading ? (
            <p className="mt-4 text-slate-300">Loading pools…</p>
          ) : poolRows.length ? (
            <div className="mt-4 space-y-3">
              {poolRows.map((pool, index) => (
                <div key={`${poolId(pool)}-${index}`} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium text-white">{poolId(pool)}</p>
                      <p className="mt-1 text-sm text-slate-400">
                        {formatValue(pool?.description || pool?.pair || pool?.symbol, 'Pool metadata')}
                      </p>
                    </div>
                    <span className="rounded-full bg-white/5 px-3 py-1 text-xs text-slate-300">
                      {formatValue(pool?.price || pool?.rate || pool?.liquidity, 'Live')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-4 text-slate-300">No pools were returned by the exchange endpoint.</p>
          )}
        </article>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <article className="glass p-6">
          <h2 className="text-xl font-semibold text-white">Wrap endpoint</h2>
          <p className="mt-3 text-slate-300">
            The backend exposes <code>/api/exchange/wrap</code>, but the current response contract is
            not documented in the frontend. Use this endpoint carefully in controlled environments.
          </p>
        </article>

        <article className="glass p-6">
          <h2 className="text-xl font-semibold text-white">Liquidity endpoint</h2>
          <p className="mt-3 text-slate-300">
            The backend also exposes <code>/api/exchange/liquidity</code>. This screen keeps it
            informational to avoid guessing unsafe payload shapes from the UI.
          </p>
        </article>
      </section>
    </div>
  );
}
